import React from "react"
import { Col, Row, Table } from "react-bootstrap"
import { useSelector } from "react-redux"
import _ from "lodash"

import FormikBootstrap from "@/components/formik-bootstrap"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import ComponentRatesView from "@/onboarding/features/product/components/shared/ComponentRatesView"
import Routes from "@/routes"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import { ReferencedValueTypes } from "@/onboarding/features/product/models/Product"
import EditDetailsAndRates from "@/onboarding/features/product/components/shared/EditDetailsAndRates"
import ComponentOperatorSelect from "@/onboarding/features/product/components/shared/ComponentOperatorSelect"
import { templateRenders } from "@/onboarding/features/product/selectors"

import OperatorDisplay from "./OperatorDisplay"
import TenderStatus from "./TenderStatus"

const MenuSampleDownload = ({ menuSourceFileId }): JSX.Element => {
  if (_.isEmpty(menuSourceFileId)) return <>N/A</>

  const menuSrc = typeof menuSourceFileId === "string" ? menuSourceFileId : menuSourceFileId.value
  if (!_.isEmpty(menuSrc)) {
    return (
      <a href={Routes.file_holder_path({ id: menuSrc })} download className="text-underline">
        Download
      </a>
    )
  }

  return <>N/A</>
}

const WriteView = ({
  hideRates = false,
  showOperator = false,
  referencedValueMessage = "",
  tender = null,
}): JSX.Element => {
  const showMenuSrc = useSelector(templateRenders("refreshment", "menu_src"))

  return (
    <EditDetailsAndRates hideRates={hideRates}>
      <h5 className="mb-0">
        Edit Details
        <TenderStatus tender={tender} />
      </h5>
      <Row>
        <Col md={5}>
          {/*
              The name field is required.
              There is an open feature request for extracting requiredness from the schema: https://github.com/jaredpalmer/formik/issues/1241
            */}
          <FormikBootstrap.TextInput name="name" label="Name *" />
        </Col>
        <Col md={5}>
          <FormikBootstrap.TextInput name="description" label="Description" />
        </Col>
      </Row>
      <Row>
        <Col md={5}>
          <ReferencedValueSelect
            name="refreshmentType"
            type={ReferencedValueTypes.REFRESHMENT_TYPE}
            label="Refreshment Type"
            emptyMessage={referencedValueMessage}
          />
        </Col>
        {showOperator && (
          <Col md={5}>
            <ComponentOperatorSelect />
          </Col>
        )}
      </Row>
      {showMenuSrc && (
        <Row>
          <Col md={5}>
            <FormikBootstrap.FileInput name="menuSrc" label="Sample Menu" />
          </Col>
        </Row>
      )}
    </EditDetailsAndRates>
  )
}

const ReadView = ({
  component,
  hideRates,
  showOperator,
  tender,
  previousValues = false,
}): JSX.Element => {
  const textStyle = previousValues ? "original-product-text" : ""
  const showMenuSrc = useSelector(templateRenders("refreshment", "menu_src"))

  return (
    <div className="d-flex flex-column gap-2">
      <h5 className={textStyle}>
        Details
        <TenderStatus tender={tender} />
      </h5>
      <Table>
        <thead>
          <tr>
            <th className={textStyle}>Refreshment Type</th>
            <th className={textStyle}>Name</th>
            <th className={textStyle}>Description</th>
            {showOperator && <th className={textStyle}>Operator</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Refreshment Type">
              <TextDisplay value={component.refreshmentType} previousValue={previousValues} />
            </td>
            <td aria-label="Name">
              <TextDisplay value={component.name} previousValue={previousValues} />
            </td>
            <td aria-label="Description">
              <TextDisplay value={component.description} previousValue={previousValues} />
            </td>
            {showOperator && (
              <td aria-label="Operator">
                <OperatorDisplay operatorId={component.operatorId} previousValue={previousValues} />
              </td>
            )}
          </tr>
        </tbody>
        {showMenuSrc && (
          <>
            <thead>
              <tr>
                <th className={textStyle} colSpan={3}>
                  Sample Menu
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td aria-label="Sample Menu" colSpan={3}>
                  <MenuSampleDownload menuSourceFileId={component.menuSrc} />
                </td>
              </tr>
            </tbody>
          </>
        )}
      </Table>
      {!hideRates && (
        <Col md={3}>
          <h5 className={`mt-2 ${textStyle}`}>Rates</h5>
          <ComponentRatesView ratesheet={component.ratesheet} previousValues={previousValues} />
        </Col>
      )}
    </div>
  )
}

const View = ({
  component,
  previousValues = false,
  editing = false,
  hideRates = false,
  showOperator = false,
  tender = null,
  referencedValueMessage = "",
}): JSX.Element =>
  editing ? (
    <WriteView
      hideRates={hideRates}
      showOperator={showOperator}
      referencedValueMessage={referencedValueMessage}
      tender={tender}
    />
  ) : (
    <ReadView
      component={component}
      hideRates={hideRates}
      showOperator={showOperator}
      previousValues={previousValues}
      tender={tender}
    />
  )

export default View
